import { cloneElement, ReactElement } from 'react';
import { Field, Form } from 'react-final-form';
import { Link } from 'react-router-dom';
import { isBloggerBusiness } from '../../utils/accessService';
import {
    ymTrackSwitchProductsAutoImport,
    ymTrackSwitchProductsAutoParser,
} from '../../utils/metrika';
import { getLinkToPersonalData } from '../../utils/redirectToPersonalData';
import {
    excludeCyrillicRegExp,
    excludeSymbolsRegExp,
    instagramProfileRegExp,
    publicIdRegExp,
} from '../../utils/regexps';
import { useTranslation } from '../../utils/useTranslation';
import { composeValidators } from '../../utils/validators/compose';
import { makeClickOutLinkValidator } from '../../utils/validators/makeClickOutLinkValidator';
import { makeEmptyValidator } from '../../utils/validators/makeEmptyValidator';
import { makeMaxLengthValidator } from '../../utils/validators/makeMaxLengthValidator';
import { makeRegExpValidator } from '../../utils/validators/makeRegExpValidator';
import { makeRequiredValidator } from '../../utils/validators/makeRequiredValidator';
import { makeUrlValidator } from '../../utils/validators/makeUrlValidator';
import { Validator } from '../../utils/validators/types';
import { confirmAlert } from '../feedback/Alert';
import { WithHint } from '../feedback/Tooltip';
import { IconChevron } from '../icon/IconChevron';
import { Button } from '../input/Button';
import { FormControl } from '../input/FormControl';
import { FormControlGroup } from '../input/FormControlGroup';
import { FormControlLabel, LabelPlacement } from '../input/FormControlLabel';
import { Input } from '../input/Input';
import { Radio } from '../input/Radio';
import { Switch } from '../input/Switch';
import { TextArea } from '../input/TextArea';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Typography } from '../typography/Typography';
import type { BusinessPersistenceUI, BusinessType } from './types';

const DESCRIPTION_MAX_LENGTH = 300;

interface BusinessPersistenceFormProps {
    initialValues: Partial<BusinessPersistenceUI>;
    onSubmit: (formValues: BusinessPersistenceUI) => Promise<void>;
    membersSlot?: ReactElement;
    categoriesSlot: ReactElement;
    categoriesChanged: boolean;
    canEditPublicId: boolean;
    canEditInstagramUrl: boolean;
    canEditSiteUrl: boolean;
    canEditBusiness: boolean;
    canEditBusinessCategories: boolean;
    canEditProducts?: boolean;
    isPublicIdVisible?: boolean;
    isSiteVisible?: boolean;
    needChangeNameInPassport?: boolean;
    businessType?: BusinessType;
    login?: string;
    publicId?: string;
    geoSlot?: ReactElement;
    geoChanged?: boolean;
    selectedRegions?: Record<string, string>;
    onSupportClick?: VoidFunction;
    onFieldBlur: (fieldName: string, value: string) => void;
}

const validateDisplayName = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(excludeSymbolsRegExp, 'Не используйте запрещенные символы  ; > < /'),
    makeMaxLengthValidator(25, 'Название профиля не должно превышать 25 символов'),
);

const validatePublicId = composeValidators(
    makeRequiredValidator<string>(),
    makeRegExpValidator(
        publicIdRegExp,
        'Имя профиля должно начинаться с символа @ и содержать как минимум один символ',
    ),
    makeRegExpValidator(excludeSymbolsRegExp, 'Не используйте запрещенные символы  ; > < /'),
    makeRegExpValidator(excludeCyrillicRegExp, 'Не используйте кириллические символы'),
);

const validateDescription = composeValidators(
    makeEmptyValidator(),
    makeMaxLengthValidator(
        DESCRIPTION_MAX_LENGTH,
        `Описание профиля не должно превышать ${DESCRIPTION_MAX_LENGTH} символов`,
    ),
);

const validateSiteDisplay = composeValidators(
    makeRequiredValidator(),
    makeUrlValidator(),
    makeClickOutLinkValidator('В качестве сайта вы можете использовать ваш сайт или маркетплейс.'),
);

interface SourceFieldsProps {
    sourceLabel: string;
    urlFieldProps?: {
        urlSourceName?: string;
        urlValue?: string;
        urlPlaceHolder?: string;
        urlValidator?: Validator<string>;
    };
    importFormatPath: string;
    messageSlot: ReactElement;
    disabled: boolean;
    canEditBusiness: boolean;
    onFieldBlur: (fieldName: string, value: string) => void;
    disabledImportPublish: boolean;
    isBlogger: boolean;
}

const ImportSourceFieldset = (props: SourceFieldsProps) => {
    const t = useTranslation();
    const {
        sourceLabel,
        urlFieldProps = {},
        importFormatPath,
        messageSlot,
        disabled,
        canEditBusiness,
        onFieldBlur,
        disabledImportPublish,
        isBlogger,
    } = props;
    const { urlSourceName, urlValue, urlPlaceHolder, urlValidator } = urlFieldProps;
    const accountText = isBlogger ? 'ваш профиль' : 'профиль вашего бизнеса';

    return (
        <>
            <Flex direction="row" py={2} spacing={0.5} alignItems="center">
                <WithHint
                    message={t(
                        `Используйте ссылки только на ${accountText}.\n\nПосты будут загружаться со следующего дня после добавления источника.\n\nПосты содержащие запрещённые слова будут скачиваться в черновики.\n\nЗапрещены любые упоминания сервисов Meta*, розыгрыши, призывы писать в комментарии.`,
                    )}
                >
                    <Typography variant="body" weight="bold">
                        {sourceLabel}
                    </Typography>
                </WithHint>
            </Flex>
            {urlSourceName ? (
                <Field
                    name={urlSourceName}
                    validate={urlValidator}
                    render={({ input, meta }) => (
                        <FormControl
                            pt={1}
                            pb={urlValue ? 1 : 2}
                            label={t('Ссылка на профиль')}
                            error={meta.touched ? Boolean(meta.error) : undefined}
                            message={meta.touched ? meta.error : undefined}
                            disabled={disabled}
                        >
                            <Input
                                {...input}
                                type="text"
                                autoComplete="off"
                                placeholder={urlPlaceHolder}
                            />
                        </FormControl>
                    )}
                />
            ) : null}
            {cloneElement(messageSlot)}
            {urlValue ? (
                <Field
                    name={importFormatPath}
                    render={({ input }) => (
                        <FormControlGroup width="100%" {...input}>
                            {!disabledImportPublish && (
                                <FormControlLabel
                                    label={t('Скачивать и публиковать')}
                                    borderBottom="2px solid var(--divider)"
                                    control={
                                        <Radio
                                            value="import_publish"
                                            onChange={(event) => {
                                                input.onChange(event);
                                                onFieldBlur(
                                                    'import_publish',
                                                    event.currentTarget.checked ? 'checked' : '',
                                                );
                                            }}
                                        />
                                    }
                                    disabled={!canEditBusiness}
                                />
                            )}
                            <FormControlLabel
                                label={t('Скачивать и сохранять в черновики')}
                                borderBottom="2px solid var(--divider)"
                                control={
                                    <Radio
                                        value="import_draft"
                                        onChange={(event) => {
                                            input.onChange(event);
                                            onFieldBlur(
                                                'import_draft',
                                                event.currentTarget.checked ? 'checked' : '',
                                            );
                                        }}
                                    />
                                }
                                disabled={!canEditBusiness}
                            />
                            <FormControlLabel
                                label={t('Не скачивать')}
                                control={
                                    <Radio
                                        value="do_not_import"
                                        onChange={(event) => {
                                            input.onChange(event);
                                            onFieldBlur(
                                                'do_not_import',
                                                event.currentTarget.checked ? 'checked' : '',
                                            );
                                        }}
                                    />
                                }
                                disabled={!canEditBusiness}
                            />
                        </FormControlGroup>
                    )}
                />
            ) : null}
        </>
    );
};

export const BusinessPersistenceForm = (props: BusinessPersistenceFormProps) => {
    const {
        initialValues,
        membersSlot,
        categoriesSlot,
        categoriesChanged,
        onSubmit,
        canEditInstagramUrl,
        canEditSiteUrl,
        canEditPublicId,
        canEditBusiness,
        canEditBusinessCategories,
        canEditProducts,
        isPublicIdVisible,
        isSiteVisible,
        needChangeNameInPassport,
        businessType,
        login,
        publicId,
        geoSlot,
        geoChanged,
        selectedRegions,
        onSupportClick,
        onFieldBlur,
    } = props;
    const t = useTranslation();
    const isBlogger = isBloggerBusiness(businessType);

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            mutators={{
                setUseProductsAutoImport: (args, state, utils) => {
                    utils.changeValue(state, 'useProductsAutoImport', () => false);
                },
                setUseProductsAutoParser: (args, state, utils) => {
                    utils.changeValue(state, 'useProductsDisableAutoParser', () => true);
                },
            }}
            render={({
                submitting,
                pristine,
                invalid,
                submitError,
                handleSubmit,
                values,
                form,
            }) => (
                <Flex
                    as="form"
                    width="100%"
                    height={{ xs: '100%', sm: 'auto' }}
                    onSubmit={handleSubmit}
                    spacing={1}
                >
                    <Flex
                        px={2}
                        width="100%"
                        height={{ xs: '100%', sm: 'auto' }}
                        borderRadius={2}
                        backgroundColor="var(--background-primary)"
                    >
                        <WithHint
                            message={
                                'Заполните профиль так, чтобы пользователям был понятен и интересен ваш продукт.\n\nОписание не должно содержать нецензурную лексику и жаргоны.'
                            }
                        >
                            <Typography variant="title3" weight="bold" py={2}>
                                {t('Основная информация')}
                            </Typography>
                        </WithHint>

                        {isPublicIdVisible ? (
                            <>
                                <Field
                                    name="publicId"
                                    validate={validatePublicId}
                                    render={({ input, meta }) => (
                                        <FormControl
                                            py={1}
                                            required
                                            label={t('Имя профиля')}
                                            error={meta.touched ? Boolean(meta.error) : undefined}
                                            message={meta.touched ? meta.error : undefined}
                                            disabled={!canEditPublicId}
                                        >
                                            <Input
                                                {...input}
                                                type="text"
                                                autoComplete="off"
                                                placeholder={t('Не заполнено')}
                                            />
                                        </FormControl>
                                    )}
                                />
                                <Typography variant="caption" color="secondary">
                                    {t(
                                        'Название может содержать только латинские строчные буквы, цифры, точки или нижнее подчеркивание.',
                                    )}
                                </Typography>
                                <Typography variant="caption" color="secondary" pb={2}>
                                    {t('Имя профиля можно изменить только через ')}
                                    <Typography
                                        display="inline"
                                        as={Link}
                                        to={
                                            process.env.REACT_APP_SUPPORT_FORM_LINK
                                                ? `${process.env.REACT_APP_SUPPORT_FORM_LINK}?login=${login}`
                                                : ''
                                        }
                                        color="link"
                                        textDecoration="none"
                                        variant="caption"
                                        onClick={onSupportClick}
                                    >
                                        {t('поддержку')}
                                    </Typography>
                                </Typography>
                            </>
                        ) : null}
                        <Field
                            name="displayName"
                            validate={isBlogger ? undefined : validateDisplayName}
                            render={({ input, meta }) => (
                                <FormControl
                                    py={1}
                                    required
                                    disabled={!canEditBusiness}
                                    label={t(isBlogger ? 'Имя профиля' : 'Название бизнеса')}
                                    error={meta.touched ? Boolean(meta.error) : undefined}
                                    message={meta.touched ? meta.error : undefined}
                                >
                                    {needChangeNameInPassport ? (
                                        <Flex
                                            direction="row"
                                            width="100%"
                                            as={Link}
                                            target="_blank"
                                            to={getLinkToPersonalData()}
                                            textDecoration="none"
                                        >
                                            <Typography
                                                display="inline"
                                                width="100%"
                                                variant="body"
                                            >
                                                {input.value}
                                            </Typography>
                                            <IconChevron
                                                style={{ transform: 'rotate(270deg)' }}
                                                size={20}
                                                color="var(--color-primary)"
                                            />
                                        </Flex>
                                    ) : (
                                        <Input
                                            {...input}
                                            type="text"
                                            autoComplete="off"
                                            placeholder={t('Не заполнено')}
                                            onBlur={(event) => {
                                                onFieldBlur('displayName', event.target.value);
                                                input.onBlur(event);
                                            }}
                                        />
                                    )}
                                </FormControl>
                            )}
                        />
                        <Field
                            name="description"
                            validate={validateDescription}
                            render={({ input, meta }) => (
                                <FormControl
                                    py={1}
                                    label={t('Описание профиля')}
                                    disabled={!canEditBusiness}
                                    error={meta.touched ? Boolean(meta.error) : undefined}
                                    message={meta.touched ? meta.error : undefined}
                                >
                                    <TextArea
                                        {...input}
                                        rows={4}
                                        autoComplete="off"
                                        placeholder={t('Не заполнено')}
                                        onBlur={(event) => {
                                            onFieldBlur('description', event.target.value);
                                            input.onBlur(event);
                                        }}
                                    />
                                </FormControl>
                            )}
                        />
                        {isSiteVisible ? (
                            <>
                                <Field
                                    name="links.siteDisplay"
                                    validate={validateSiteDisplay}
                                    render={({ input, meta }) => (
                                        <FormControl
                                            py={1}
                                            label={t('Ссылка на сайт')}
                                            hint={t(
                                                'Укажите ссылку на ваш сайт или его раздел.\n\nСсылка не должна содержать utm_source, сервис добавит данную  метку самостоятельно.',
                                            )}
                                            error={meta.touched ? Boolean(meta.error) : undefined}
                                            disabled={!canEditSiteUrl}
                                            message={meta.touched ? meta.error : undefined}
                                            required
                                        >
                                            <Input
                                                {...input}
                                                type="text"
                                                autoComplete="off"
                                                placeholder="https://"
                                                onBlur={(event) => {
                                                    onFieldBlur(
                                                        'links.siteDisplay',
                                                        event.target.value,
                                                    );
                                                    input.onBlur(event);
                                                }}
                                            />
                                        </FormControl>
                                    )}
                                />
                                {!canEditSiteUrl && (
                                    <Typography variant="caption" color="secondary" pb={2}>
                                        {t('Ссылку на сайт можно изменить только через ')}
                                        <Typography
                                            display="inline"
                                            as={Link}
                                            to={
                                                process.env.REACT_APP_SUPPORT_FORM_LINK
                                                    ? `${process.env.REACT_APP_SUPPORT_FORM_LINK}?login=${login}`
                                                    : ''
                                            }
                                            color="link"
                                            textDecoration="none"
                                            variant="caption"
                                            onClick={onSupportClick}
                                        >
                                            {t('поддержку')}
                                        </Typography>
                                    </Typography>
                                )}
                            </>
                        ) : null}
                        <Field
                            type="checkbox"
                            name="hideInFeed"
                            render={({ input }) => (
                                <FormControlLabel
                                    label={
                                        <Box onClick={(e) => e.preventDefault()}>
                                            <WithHint
                                                inlineIcon
                                                message={
                                                    'При включении этого режима пользователям не будут рекомендоваться ваши посты.\n\nЧерез поиск и по прямой ссылке ваш профиль можно будет найти.'
                                                }
                                            >
                                                {t('Скрыть посты из ленты пользователей')}
                                            </WithHint>
                                        </Box>
                                    }
                                    disabled={!canEditBusiness}
                                    control={
                                        <Switch
                                            {...input}
                                            onChange={(event) => {
                                                input.onChange(event);
                                                onFieldBlur(
                                                    'hideInFeed',
                                                    event.currentTarget.checked ? 'checked' : '',
                                                );
                                            }}
                                        />
                                    }
                                    labelPlacement={LabelPlacement.LEFT}
                                />
                            )}
                        />
                    </Flex>
                    {canEditBusinessCategories ? cloneElement(categoriesSlot) : null}
                    {geoSlot ? cloneElement(geoSlot) : null}
                    <Flex
                        width="100%"
                        spacing={0.5}
                        px={2}
                        height={{ xs: '100%', sm: 'auto' }}
                        borderRadius={2}
                        backgroundColor="var(--background-primary)"
                    >
                        <Typography variant="title3" weight="bold" py={2}>
                            {t('Источники контента')}
                        </Typography>

                        <ImportSourceFieldset
                            sourceLabel="Instagram*"
                            disabled={!canEditInstagramUrl}
                            canEditBusiness={canEditBusiness}
                            onFieldBlur={onFieldBlur}
                            urlFieldProps={{
                                urlSourceName: 'instagram.url',
                                urlValue: values.instagram?.url,
                                urlPlaceHolder: 'https://www.instagram.com/username',
                                urlValidator: composeValidators(
                                    makeUrlValidator(),
                                    makeRegExpValidator(
                                        instagramProfileRegExp,
                                        'Укажите ссылку в формате https://www.instagram.com/username',
                                    ),
                                ),
                            }}
                            messageSlot={
                                <Typography variant="caption" color="secondary" pb={2}>
                                    {t('Ссылку на instagram можно изменить только через ')}
                                    <Typography
                                        display="inline"
                                        as={Link}
                                        to={
                                            process.env.REACT_APP_SUPPORT_FORM_LINK
                                                ? `${process.env.REACT_APP_SUPPORT_FORM_LINK}?login=${login}`
                                                : ''
                                        }
                                        color="link"
                                        textDecoration="none"
                                        variant="caption"
                                        onClick={onSupportClick}
                                    >
                                        {t('поддержку')}
                                    </Typography>
                                </Typography>
                            }
                            importFormatPath="instagram.importFormat"
                            disabledImportPublish={isBlogger}
                            isBlogger={isBlogger}
                        />
                    </Flex>

                    <Typography px={2} color="secondary" variant="caption">
                        {t(
                            '*Meta, владеющая Instagram, признана экстремистской организацией на территории РФ',
                        )}
                    </Typography>

                    {canEditProducts && !isBlogger ? (
                        <Flex
                            width="100%"
                            spacing={0.5}
                            px={2}
                            pb={2}
                            height={{ xs: '100%', sm: 'auto' }}
                            borderRadius={2}
                            backgroundColor="var(--background-primary)"
                        >
                            <Typography variant="title3" weight="bold" py={2}>
                                {t('Товары β')}
                            </Typography>
                            <Field
                                type="checkbox"
                                name="useProductsAutoImport"
                                render={({ input }) => (
                                    <FormControlLabel
                                        label={
                                            <Box onClick={(e) => e.preventDefault()}>
                                                <WithHint
                                                    inlineIcon
                                                    message={
                                                        'Мы будем использовать технологии компьютерного зрения для распознавания товаров бизнеса на основе данных о нем, включая товарные фиды и иную доступную информацию. Если получится распознать товар, то на посте появится его превью со ссылкой. По ней пользователь сможет перейти в товар на сайте того бизнеса, который создал пост, и приобрести его. Для распознавания товаров не используются товары других продавцов.'
                                                    }
                                                >
                                                    {t('Автоматически добавлять товары к постам')}
                                                </WithHint>
                                            </Box>
                                        }
                                        disabled={!canEditBusiness}
                                        control={
                                            <Switch
                                                {...input}
                                                onChange={async (event) => {
                                                    ymTrackSwitchProductsAutoImport({
                                                        page: 'Настройки бизнеса',
                                                        userLogin: login,
                                                        businessId: publicId,
                                                    });
                                                    if (event.currentTarget.checked) {
                                                        input.onChange(event);
                                                    } else {
                                                        try {
                                                            await confirmAlert(
                                                                'Отключение автодобавления',
                                                                'При выключении автоматического распознавания товаров все ранее добавленные товары будут удалены.',
                                                                'отмена',
                                                                'всё равно отключить',
                                                            );
                                                            form.mutators.setUseProductsAutoImport();
                                                        } catch (error) {
                                                            return;
                                                        }
                                                    }
                                                }}
                                            />
                                        }
                                        labelPlacement={LabelPlacement.LEFT}
                                    />
                                )}
                            />
                            <Field
                                type="checkbox"
                                name="useProductsDisableAutoParser"
                                render={({ input }) => (
                                    <FormControlLabel
                                        labelVerticalPadding={0}
                                        label={
                                            <Box onClick={(e) => e.preventDefault()}>
                                                <WithHint
                                                    inlineIcon
                                                    message={
                                                        'К посту можно будет прикрепить только товары, которые занесены в качестве товарных фидов в базу Яндекс Директа и (или) Яндекс Товаров.'
                                                    }
                                                >
                                                    {t(
                                                        'Использовать для распознавания товаров только фиды Яндекс Директа и Яндекс Товаров',
                                                    )}
                                                </WithHint>
                                            </Box>
                                        }
                                        disabled={!canEditBusiness}
                                        control={
                                            <Switch
                                                {...input}
                                                onChange={async (event) => {
                                                    ymTrackSwitchProductsAutoParser({
                                                        page: 'Настройки бизнеса',
                                                        userLogin: login,
                                                        businessId: publicId,
                                                    });
                                                    if (event.currentTarget.checked) {
                                                        try {
                                                            await confirmAlert(
                                                                'Отображение товаров',
                                                                'В постах будут отображаться только данные из товарных фидов Яндекс Директа и Яндекс Товаров. Из ранее созданных постов могут пропасть товарные пины, убедитесь, что все прикрепленные товары находятся в актуальных фидах. Добавить к посту товар, не внесенный в соответствующие базы, будет невозможно.',
                                                                'отмена',
                                                                'всё равно включить',
                                                            );
                                                            form.mutators.setUseProductsAutoParser();
                                                        } catch (error) {
                                                            return;
                                                        }
                                                    } else {
                                                        input.onChange(event);
                                                    }
                                                }}
                                            />
                                        }
                                        labelPlacement={LabelPlacement.LEFT}
                                    />
                                )}
                            />
                        </Flex>
                    ) : null}
                    {membersSlot ? cloneElement(membersSlot) : null}
                    <Flex width="100%" pb={2} pt={1}>
                        <Box mt="auto" width="100%">
                            {submitError ? (
                                <Typography variant="caption" color="error" mb={2}>
                                    {submitError}
                                </Typography>
                            ) : null}
                            <Button
                                width="100%"
                                type="submit"
                                size="l"
                                disabled={
                                    (pristine && !categoriesChanged && !geoChanged) ||
                                    submitting ||
                                    invalid ||
                                    !canEditBusiness ||
                                    (geoSlot && Object.keys(selectedRegions || {}).length < 1)
                                }
                            >
                                {t('Сохранить')}
                            </Button>
                        </Box>
                    </Flex>
                </Flex>
            )}
        />
    );
};
